import { useEffect } from "react"
import { DayPicker } from "react-day-picker"
import styled from "styled-components"

const MONTHS = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
]
const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"]

const pad = (n: number) => String(n).padStart(2, "0")
/** Date local → YYYY-MM-DD (sem timezone). */
const toISO = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
/** YYYY-MM-DD → Date local (meia-noite). */
const fromISO = (s: string) => {
  const [y, m, d] = s.split("-").map(Number)
  return new Date(y, m - 1, d)
}

interface MonthCalendarProps {
  value: string | null // YYYY-MM-DD
  onChange: (isoDate: string) => void
  /** menor data selecionável (YYYY-MM-DD); dias anteriores ficam desabilitados. */
  minISO?: string
  /** se fornecido, só estes dias (YYYY-MM-DD) ficam habilitados (vêm do backend). */
  enabledDays?: Set<string>
  /** avisa o intervalo do mês visível (para buscar os dias disponíveis). */
  onMonthChange?: (fromISO: string, toISO: string) => void
}

/** Calendário mensal (pt-BR) sobre o `react-day-picker` (mesma lib do admin/design).
    A disponibilidade continua vindo dos dados: `minISO` + `enabledDays` (do backend)
    definem quais dias ficam desabilitados — nada é hardcoded. */
export const MonthCalendar = ({
  value,
  onChange,
  minISO,
  enabledDays,
  onMonthChange,
}: MonthCalendarProps) => {
  const selected = value ? fromISO(value) : undefined
  const defaultMonth = selected ?? (minISO ? fromISO(minISO) : new Date())
  // Não navegar para meses no passado (todos os dias já estão desabilitados) — o rdp
  // desabilita a seta de voltar no mês limite, e o CSS a esconde.
  const earliest = minISO ? fromISO(minISO) : new Date()
  const startMonth = new Date(earliest.getFullYear(), earliest.getMonth(), 1)

  const isDisabled = (date: Date): boolean => {
    const dISO = toISO(date)
    if (minISO && dISO < minISO) return true
    if (enabledDays && !enabledDays.has(dISO)) return true
    return false
  }

  const emitRange = (month: Date) => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1)
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0)
    onMonthChange?.(toISO(first), toISO(last))
  }

  // O DayPicker só avisa a mudança de mês na navegação; disparamos o mês inicial no mount.
  useEffect(() => {
    emitRange(defaultMonth)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Root>
      <DayPicker
        mode="single"
        selected={selected}
        onSelect={(d) => {
          if (d) onChange(toISO(d))
        }}
        disabled={isDisabled}
        defaultMonth={defaultMonth}
        startMonth={startMonth}
        onMonthChange={emitRange}
        showOutsideDays
        formatters={{
          formatCaption: (m) => `${MONTHS[m.getMonth()]} ${m.getFullYear()}`,
          formatWeekdayName: (d) => WEEKDAYS[d.getDay()],
        }}
      />
    </Root>
  )
}

/** Estiliza o react-day-picker via as classes `.rdp-*` (sem importar o CSS da lib —
    o bloco é self-contained). O `<table>` do rdp já dá a grade nativa. */
const Root = styled.div`
  background: var(--td-input-bg, var(--cards-background, #fff));
  border: 1px solid var(--td-field-border, var(--general-border, #b7c0cd));
  border-radius: 12px;
  padding: 0.75rem;
  --rdp-accent-color: var(--general-primary, #0027d4);
  --rdp-day-width: 2.5rem;
  --rdp-day-height: 2.5rem;

  .rdp-root {
    margin: 0;
  }

  .rdp-months {
    position: relative;
    display: flex;
  }
  .rdp-month {
    width: 100%;
  }

  /* Cabeçalho: título à esquerda, navegação à direita. */
  .rdp-month_caption {
    display: flex;
    align-items: center;
    min-height: 32px;
    margin-bottom: 0.5rem;
  }
  .rdp-caption_label {
    font-weight: 600;
    font-size: 1rem;
    color: var(--text-title, #0f172a);
    text-transform: capitalize;
  }
  .rdp-nav {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    gap: 4px;
    z-index: 1;
  }
  .rdp-button_previous,
  .rdp-button_next {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    border: 1px solid var(--general-border, #b7c0cd);
    background: transparent;
    color: var(--text-title, #0f172a);
    cursor: pointer;
  }
  /* O rdp v9 marca a navegação no limite com aria-disabled (não o atributo disabled). */
  .rdp-button_next:disabled,
  .rdp-button_next[aria-disabled="true"] {
    opacity: 0.4;
    cursor: not-allowed;
  }
  /* Sem mês anterior navegável (mês atual é o limite) → esconde a seta de voltar. */
  .rdp-button_previous:disabled,
  .rdp-button_previous[aria-disabled="true"] {
    visibility: hidden;
  }
  .rdp-chevron {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }

  .rdp-month_grid {
    width: 100%;
    border-collapse: collapse;
  }

  .rdp-weekday {
    font-size: 0.75rem;
    font-weight: 400;
    color: var(--text-body, #94a3b8);
    padding: 6px 0;
    text-transform: none;
    opacity: 1;
  }

  .rdp-day {
    padding: 2px;
    text-align: center;
  }

  /* Dia disponível (não-selecionado) = fundo cinza claro, como o design.
     Largura fluida (100% da célula, teto de --rdp-day-width) com proporção
     quadrada — em telas estreitas encolhe em vez de estourar o container. */
  .rdp-day_button {
    width: 100%;
    max-width: var(--rdp-day-width);
    aspect-ratio: 1 / 1;
    margin: 0 auto;
    border: none;
    border-radius: 10px;
    font-size: 0.9375rem;
    font-weight: 500;
    color: var(--text-title, #0f172a);
    background: var(--general-background, #f1f5f9);
    cursor: pointer;
  }
  .rdp-day_button:hover:not([disabled]) {
    background: var(--general-border, #e2e8f0);
  }

  /* Selecionado = cor primária. */
  .rdp-selected .rdp-day_button,
  .rdp-day_button[aria-selected="true"] {
    background: var(--general-primary, #0027d4);
    color: var(--buttons-text, #fff);
  }

  /* Indisponível (desabilitado) e dias de outros meses = sem fundo, apagado. */
  .rdp-disabled .rdp-day_button,
  .rdp-day_button[disabled],
  .rdp-outside .rdp-day_button {
    background: transparent;
    color: var(--text-body, #94a3b8);
    opacity: 0.4;
    cursor: not-allowed;
  }
`
